/* eslint-disable @typescript-eslint/no-explicit-any */
// ==============================
// Imports
// ==============================

// MUI Components
import { SelectChangeEvent } from '@mui/material';

// Libraries
import React, { useCallback, useEffect, useState } from 'react';

// Local Components
import { apiGet, apiPost, apiPut } from '../utils/apiClient';
import {
  // notifyActionSuccess,
  notifyAPIClient,
} from '../utils/toast';

// Types
import {
  AttendanceRequest,
  AttendanceResponse,
  AttendanceUndoRequest,
  PaginatedResponse,
  Registration,
} from '../types/AssessmentTable.type';
import useModal from './useModal';
import {
  UserStageProgress,
  Response,
  // SuccessResponse,
  CourseLevel,
} from '../types/CourseStageAccordion.type';
import {
  ServiceType,
  Subsubtype,
  Subtype,
} from '../types/ServiceAccordion.type';
import dayjs from 'dayjs';
import { exportRowsToXlsx } from '../utils/xlsxExport';

export const useAssessmentController = () => {
  // States
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [courseStages, setCourseStages] = useState<
    {
      level: string;
      stages: UserStageProgress[];
    }[]
  >([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [serviceTypeFilter, setServiceTypeFilter] = useState<ServiceType>({
    id: '-1',
    name: 'All',
    subtypes: [],
  });
  const [subTypeFilter, setSubTypeFilter] = useState<Subtype>({
    id: '-1',
    name: 'All',
    subsubtypes: [],
  });
  const [subSubTypeFilter, setSubSubTypeFilter] = useState<Subsubtype>({
    id: '-1',
    name: 'All',
    serviceFor: 'CHILD',
    price: 0,
    duration: 0,
  });
  const [serviceOptions, setServiceOptions] = useState<ServiceType[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  // 'all' | 'anonymous' | 'non_anonymous'
  const [anonymousFilter, setAnonymousFilter] = useState<string>('all');
  // 'all' | 'available' (students with the join-assessment toggle, i.e. COURSE)
  const [joinableFilter, setJoinableFilter] = useState<string>('all');
  const [downloadLoading, setDownloadLoading] = useState(false);

  const [levelOptions, setLevelOptions] = useState<CourseLevel[]>([]);
  const [levelFilter, setLevelFilter] = useState<CourseLevel>({
    level: -1,
    levelName: 'All',
  });

  const { isOpen: isOpenLevelModal, toggle: toggleLevelModal } = useModal();
  const [selectedRegistration, setSelectedRegistration] =
    useState<Registration | null>(null);

  // Join Assessment Dialog
  const [openJoinAssessmentDialog, setOpenJoinAssessmentDialog] =
    useState(false);

  // Attendance Dialog
  const [openAttendanceDialog, setOpenAttendanceDialog] = useState(false);

  // Promote / Demote History Dialog
  const [openPromotionHistoryDialog, setOpenPromotionHistoryDialog] =
    useState(false);

  // Last sync time for assessment data
  const [lastSyncTime, setLastSyncTime] = useState<dayjs.Dayjs | null>(null);

  // Data-loading state for the main list fetch
  const [loading, setLoading] = useState(true);

  // Highlighted row
  const [highlightedRow, setHighlightedRow] = useState<string | null>(null);

  // const [userTypeFilter, setUserTypeFilter] = useState("");
  // const [orderBy, setOrderBy] = useState<keyof User>("id");
  // const [order, setOrder] = useState<Order>("asc");

  // Request form
  // const [formData, setFormData] = useState({
  //   id: 0,
  //   attendanceDate: '',
  //   note: ''
  // });

  // Networking
  const fetchRegistrations = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await apiGet<PaginatedResponse<Registration>>(
        `/registration/list?page=${page}&limit=${limit}&search=${searchTerm}&filter=${serviceTypeFilter?.name}&subtype=${subTypeFilter?.name}&subsubtype=${subSubTypeFilter?.name}&status=${statusFilter}&level=${levelFilter?.levelName}&anonymous=${anonymousFilter}&joinable=${joinableFilter}`,
      );

      setRegistrations(res.data);
      // console.log('Success to fetch registrations:', res);
      // handleCurrentLevel(res.data);
      setTotalItems(res.metadata.totalItems);
      setLastSyncTime(dayjs());
    } catch (error) {
      console.error('Failed to fetch registrations:', error);
    } finally {
      setLoading(false);
    }
  }, [
    page,
    limit,
    searchTerm,
    serviceTypeFilter?.name,
    subTypeFilter?.name,
    subSubTypeFilter?.name,
    statusFilter,
    levelFilter?.levelName,
    anonymousFilter,
    joinableFilter,
  ]);

  const fetchUserStageProgress = async (
    subSubTypeId: number,
    registrationId: number,
  ) => {
    try {
      const res = await apiGet<Response<UserStageProgress>>(
        `/course/get-user-progress/${subSubTypeId}/${registrationId}`,
      );
      const groupedStages = res.data.reduce(
        (
          acc: Record<string, UserStageProgress[]>,
          stage: UserStageProgress,
        ) => {
          if (!acc[stage.levelName]) {
            acc[stage.levelName] = [];
          }
          acc[stage.levelName].push(stage);
          return acc;
        },
        {},
      );

      const courseStagesArray = Object.entries(groupedStages).map(
        ([level, stages]) => ({
          level,
          stages: stages as UserStageProgress[],
        }),
      );
      setCourseStages(courseStagesArray);
    } catch (error) {
      console.error('Failed to fetch course stages:', error);
    }
  };

  const fetchServiceOptions = async () => {
    const res = await apiGet<Response<ServiceType>>(`/service/list`);
    setServiceOptions(res.data);
  };

  const fetchCourseLevels = async () => {
    const res = await apiGet<Response<CourseLevel>>('/course/get-all-levels');
    setLevelOptions(res.data);
  };

  // const handleCurrentLevel = async (data: Registration[]) => {
  //   try {
  //     const updated = await Promise.all(
  //       data.map(async (row) => {
  //         if (row.serviceType === 'COURSE') {
  //           const res = await apiGet<SuccessResponse>(
  //             `/course/get-current-level/${row.subsubtypeId}/${row.id}`,
  //             {},
  //           );
  //           return {
  //             ...row,
  //             levelName: res.data.levelName || '---',
  //           };
  //         } else {
  //           return {
  //             ...row,
  //             levelName: '---',
  //           };
  //         }
  //       }),
  //     );

  //     setRegistrations(updated);
  //   } catch (error) {
  //     console.error('Failed to get current level:', error);
  //   }
  // };

  // Hooks
  useEffect(() => {
    fetchRegistrations();
  }, [
    page,
    limit,
    serviceTypeFilter,
    subTypeFilter,
    subSubTypeFilter,
    statusFilter,
    levelFilter,
    anonymousFilter,
    joinableFilter,
  ]);

  // ==============================
  // Update 09/06/2025: Fetch registrations every 3 seconds
  // ==============================
  useEffect(() => {
    const interval = setInterval(() => {
      fetchRegistrations(true);
    }, 3000);

    return () => clearInterval(interval);
  }, [fetchRegistrations]);

  useEffect(() => {
    fetchServiceOptions();
    fetchCourseLevels();
  }, []);

  // Handlers
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(1);
    // notifyActionSuccess('search');
  };

  const handleFilterChange = (event: SelectChangeEvent<string>) => {
    const newValue = event.target.value;
    const selectedServiceType = serviceOptions.find(
      (option) => option.id === newValue,
    ) || {
      id: '-1',
      name: 'All',
      subtypes: [],
    };

    setServiceTypeFilter(selectedServiceType);
    setPage(1);
  };
  const handleFilterSubtypeChange = (event: SelectChangeEvent<string>) => {
    const newValue = event.target.value;
    const selectedServiceType = serviceOptions.find(
      (option) => option.id === serviceTypeFilter.id,
    ) || {
      id: '-1',
      name: 'All',
      subtypes: [],
    };
    const selectedSubtype = selectedServiceType.subtypes.find(
      (option) => option.id === newValue,
    ) || {
      id: '-1',
      name: 'All',
      subsubtypes: [],
    };

    setSubTypeFilter(selectedSubtype);
    setPage(1);
  };
  const handleFilterSubSubtypeChange = (event: SelectChangeEvent<string>) => {
    const newValue = event.target.value;
    const selectedServiceType = serviceOptions.find(
      (option) => option.id === serviceTypeFilter.id,
    ) || {
      id: '-1',
      name: 'All',
      subtypes: [],
    };
    const selectedSubtype = selectedServiceType.subtypes.find(
      (option) => option.id === subTypeFilter.id,
    ) || {
      id: '-1',
      name: 'All',
      subsubtypes: [],
    };
    const selectedSubSubtype = selectedSubtype.subsubtypes.find(
      (option) => option.id === newValue,
    ) || {
      id: '-1',
      name: 'All',
      serviceFor: 'CHILD',
      price: 0,
      duration: 0,
    };

    setSubSubTypeFilter(selectedSubSubtype);
    setPage(1);
  };

  const handleFilterLevelChange = (event: SelectChangeEvent<string>) => {
    const newValue = event.target.value;
    const selectedLevel = levelOptions.find(
      (option) => option.levelName === newValue,
    ) || {
      level: -1,
      levelName: 'All',
    };

    setLevelFilter(selectedLevel);
    setPage(1);
  };

  // ==============================
  // Update 09/06/2025: Clear all filters
  // ==============================
  const clearFilters = () => {
    setSearchTerm('');
    setServiceTypeFilter({
      id: '-1',
      name: 'All',
      subtypes: [],
    });
    setSubTypeFilter({
      id: '-1',
      name: 'All',
      subsubtypes: [],
    });
    setSubSubTypeFilter({
      id: '-1',
      name: 'All',
      serviceFor: 'CHILD',
      price: 0,
      duration: 0,
    });
    setStatusFilter('all');
    setAnonymousFilter('all');
    setJoinableFilter('all');
    setLevelFilter({
      level: -1,
      levelName: 'All',
    });
    setSelectedRegistration(null);

    setPage(1);
    setLimit(10);
  };

  const handleDownloadAssessments = async () => {
    if (totalItems === 0) return;

    setDownloadLoading(true);
    try {
      const params = new URLSearchParams({
        page: '1',
        limit: totalItems.toString(),
        search: searchTerm,
        filter: serviceTypeFilter?.name || 'All',
        subtype: subTypeFilter?.name || 'All',
        subsubtype: subSubTypeFilter?.name || 'All',
        status: statusFilter,
        level: levelFilter?.levelName || 'All',
        anonymous: anonymousFilter,
        joinable: joinableFilter,
      });
      const response = await apiGet<PaginatedResponse<Registration>>(
        `/registration/list?${params.toString()}`,
      );

      const rows = response.data.map((registration) => ({
        'User ID': registration.userId,
        Ticket: registration.ticket,
        'First Name': registration.user.firstName,
        'Last Name': registration.user.lastName,
        'Preferred Name': registration.user.preferredName || 'None',
        Level:
          registration.serviceType === 'COURSE' && registration.courseLevel
            ? registration.courseLevel
            : '---',
        Product: registration.serviceType,
        Type: registration.subtype,
        Details: registration.subsubtype.name,
        'Start Date': new Date(registration.sos).toLocaleDateString(),
        'End Date': new Date(registration.eos).toLocaleDateString(),
        'Ticket Type': registration.visitType.toUpperCase(),
        'Checkin Today': `${registration.attendancesToday.length} time(s) (Total checkin: ${registration.attendancesAll.length} times)`,
        'Checkin Balance':
          registration.visitType === 'count'
            ? `${registration.visitBalance} time(s)`
            : '\u221e time(s)',
        'Join Assessment':
          registration.serviceType === 'COURSE'
            ? registration.joined
              ? 'YES'
              : 'NO'
            : 'N/A',
      }));

      exportRowsToXlsx(
        rows,
        `assessment_${dayjs().format('YYYY-MM-DD')}.xlsx`,
        Date.now().toString(),
      );
    } catch (error) {
      console.error('Failed to download assessments:', error);
      notifyAPIClient(
        'Failed to download assessment data. Please try again later.',
      );
    } finally {
      setDownloadLoading(false);
    }
  };

  // Dialog 1
  const handleDialogOpen = (id: number) => {
    setSelectedId(id);
    setOpenDialog(true);
  };
  const handleDialogClose = () => setOpenDialog(false);
  const handleDialogConfirm = () => {
    // Attendance
    handleAttendance();
    setOpenDialog(false);
  };

  // Dialog 2
  const handleDialogOpenAttendance = (id: number) => {
    setSelectedId(id);
    setOpenAttendanceDialog(true);
  };
  const handleDialogCloseAttendance = () => setOpenAttendanceDialog(false);

  const handleAttendance = async () => {
    const payload: AttendanceRequest = {
      registrationId: selectedId || 0,
      attendanceDate: new Date().toISOString(),
      note: '',
    };

    try {
      const res = await apiPost<AttendanceResponse, AttendanceRequest>(
        '/attendance/create',
        payload,
      );
      notifyAPIClient(res.message);
      // Update row in state
      setRegistrations((prev) =>
        prev.map((row) =>
          row.id === selectedId
            ? {
                ...res.data,
              }
            : row,
        ),
      );
    } catch (err) {
      notifyAPIClient((err as any).response.data.message);
    }
  };

  // Dialog 3
  const handleDialogOpenJoinAssessment = (id: number) => {
    setSelectedId(id);
    setOpenJoinAssessmentDialog(true);
  };
  const handleDialogCloseJoinAssessment = () =>
    setOpenJoinAssessmentDialog(false);
  const handleDialogConfirmJoinAssessment = () => {
    // Join Assessment
    handleJoinAssessment();
    setOpenJoinAssessmentDialog(false);
  };

  const handleJoinAssessment = async () => {
    try {
      const res = await apiPut<AttendanceResponse, any>(
        `/registration/join-status/${selectedId}`,
        {},
      );
      notifyAPIClient(res.message);
      // Update row in state
      setRegistrations((prev) =>
        prev.map((row) =>
          row.id === selectedId
            ? {
                ...row,
                joined: !row.joined,
              }
            : row,
        ),
      );
    } catch (err) {
      notifyAPIClient((err as any).response.data.message);
    }
  };

  const handleUndoAttendance = async (id: number) => {
    try {
      const res = await apiPost<AttendanceResponse, AttendanceUndoRequest>(
        '/attendance/undo',
        { attendanceId: id },
      );
      notifyAPIClient(res.message);
      // Update row in state
      setRegistrations((prev) =>
        prev.map((row) =>
          row.id === selectedId
            ? {
                ...res.data,
              }
            : row,
        ),
      );
    } catch (err) {
      notifyAPIClient((err as any).response.data.message);
    }
  };

  // Promote / Demote History Dialog
  const handleDialogOpenPromotionHistoryDialog = (id: number) => {
    setSelectedId(id);
    setOpenPromotionHistoryDialog(true);
  };
  const handleDialogClosePromotionHistoryDialog = () =>
    setOpenPromotionHistoryDialog(false);

  // Level Modal
  const handleOpenLevelModal = (
    subSubTypeId: number,
    registration: Registration,
  ) => {
    fetchUserStageProgress(subSubTypeId, registration.id);
    setSelectedRegistration(registration);
    toggleLevelModal();
  };
  const handleCloseLevelModal = () => {
    setCourseStages([]);
    setSelectedRegistration(null);
    toggleLevelModal();
  };

  // const handleFilterChange = (event: React.ChangeEvent<{ value: unknown }>) => {
  //   setUserTypeFilter(event.target.value as string);
  //   setPage(1);
  //   notifyFilterSuccess();
  // };

  // const handleRequestSort = (property: keyof User) => {
  //   const isAsc = orderBy === property && order === "asc";
  //   setOrder(isAsc ? "desc" : "asc");
  //   setOrderBy(property);
  // };

  const emptyRows = limit - registrations.length;

  return {
    registrations,
    page,
    limit,
    totalItems,
    searchTerm,
    openDialog,
    selectedId,
    emptyRows,
    serviceTypeFilter,
    subTypeFilter,
    subSubTypeFilter,
    statusFilter,
    setStatusFilter,
    anonymousFilter,
    setAnonymousFilter,
    joinableFilter,
    setJoinableFilter,
    setPage,
    fetchRegistrations,
    handleChangePage,
    handleChangeRowsPerPage,
    handleSearchChange,
    handleFilterChange,
    handleFilterSubtypeChange,
    handleFilterSubSubtypeChange,
    handleDialogOpen,
    handleDialogClose,
    handleDialogConfirm,
    serviceOptions,
    clearFilters,
    handleDownloadAssessments,
    downloadLoading,

    selectedRegistration,
    isOpenLevelModal,
    handleOpenLevelModal,
    handleCloseLevelModal,
    courseStages,
    fetchUserStageProgress,

    openAttendanceDialog,
    handleDialogOpenAttendance,
    handleDialogCloseAttendance,
    handleUndoAttendance,

    handleDialogOpenJoinAssessment,
    handleDialogCloseJoinAssessment,
    openJoinAssessmentDialog,
    handleDialogConfirmJoinAssessment,

    openPromotionHistoryDialog,
    handleDialogOpenPromotionHistoryDialog,
    handleDialogClosePromotionHistoryDialog,

    handleFilterLevelChange,
    levelOptions,
    levelFilter,

    lastSyncTime,
    highlightedRow,
    setHighlightedRow,

    loading,
  };
};
